import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import {
  FieldActivityIssue,
  CreateFieldActivityIssue,
  UpdateFieldActivityIssue,
  IssueStatus,
  Severity,
  IssueCategory,
} from "types/FieldActivityIssues.types";

export interface IssueFilters {
  status?: IssueStatus[];
  severity?: Severity[];
  category?: IssueCategory[];
  safetyIssue?: boolean;
  qualityIssue?: boolean;
  assignedTo?: number;
  dateFrom?: Date;
  dateTo?: Date;
  jobId?: number;
}

class FieldActivityIssuesViewStore {
  // Observables
  selectedIssueId: number | null = null;
  isCreateDialogOpen = false;
  isViewDialogOpen = false;
  isEditMode = false;
  filters: IssueFilters = {};
  searchTerm = "";
  formData: Partial<CreateFieldActivityIssue> = {};
  isSubmitting = false;
  validationErrors: Record<string, string> = {};

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  // Actions
  setSelectedIssueId = (id: number | null) => {
    this.selectedIssueId = id;
  };

  openCreateDialog = () => {
    this.isCreateDialogOpen = true;
    this.isEditMode = false;
    this.formData = {
      status: "open",
      severity: "medium",
      safetyIssue: false,
      qualityIssue: false,
      weatherRelated: false,
      productionImpact: false,
      scheduleImpact: false,
      requiresFollowUp: false,
      qcRequired: false,
      clientNotified: false,
      isVisible: true,
      viewCount: 0,
      priority: 3,
    };
    this.validationErrors = {};
  };

  openViewDialog = (issueId: number) => {
    this.selectedIssueId = issueId;
    this.isViewDialogOpen = true;
    this.isEditMode = false;
  };

  openEditDialog = (issueId: number) => {
    const issue = this.selectedIssue;
    if (issue) {
      this.selectedIssueId = issueId;
      this.isCreateDialogOpen = true;
      this.isEditMode = true;
      this.formData = { ...issue };
      this.validationErrors = {};
    }
  };

  closeDialogs = () => {
    this.isCreateDialogOpen = false;
    this.isViewDialogOpen = false;
    this.isEditMode = false;
    this.selectedIssueId = null;
    this.formData = {};
    this.validationErrors = {};
  };

  setFilters = (filters: IssueFilters) => {
    this.filters = filters;
  };

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  updateFormData = (field: string, value: any) => {
    runInAction(() => {
      this.formData = {
        ...this.formData,
        [field]: value,
      };
      // Clear validation error for this field
      if (this.validationErrors[field]) {
        delete this.validationErrors[field];
      }
    });
  };

  validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!this.formData.jobId) {
      errors.jobId = "Job is required";
    }
    if (!this.formData.issueType) {
      errors.issueType = "Issue type is required";
    }
    if (!this.formData.title) {
      errors.title = "Title is required";
    }
    if (!this.formData.description) {
      errors.description = "Description is required";
    }
    if (!this.formData.reportedBy) {
      errors.reportedBy = "Reporter is required";
    }

    // Additional validations for safety issues
    if (this.formData.safetyIssue && !this.formData.assignedTo) {
      errors.assignedTo = "Assignment required for safety issues";
    }

    // Validate dates
    if (this.formData.targetResolutionDate) {
      const targetDate = new Date(this.formData.targetResolutionDate);
      if (targetDate < new Date()) {
        errors.targetResolutionDate =
          "Target resolution date must be in the future";
      }
    }

    this.validationErrors = errors;
    return Object.keys(errors).length === 0;
  };

  // Async actions
  uploadAttachment = async (file: File, metadata: any = {}) => {
    const { authStore } = this.rootStore;
    const azureBlobStorageStore = (this.rootStore as any).azureBlobStorageStore;

    try {
      // Step 1: Request a SAS URL from the backend
      const uploadData = {
        requestBlobUrl: true, // This triggers SAS URL generation
        fileName: file.name || "unknown.jpg",
        contentType: file.type || "image/jpeg",
        fileSize: file.size,
        metadata: {
          ...metadata,
          uploadedBy: authStore.user?.email || "unknown",
          entityType: "field-activity-issue",
          issueId: this.selectedIssueId || undefined,
        },
        tags: ["field-issue-attachment"],
        // Don't send file data when requesting blob URL
      };

      console.log("Requesting SAS URL for attachment upload...");
      const fileRecord = await azureBlobStorageStore.create(uploadData);

      // Step 2: Upload directly to Azure if we got a SAS URL
      if (fileRecord.uploadUrl) {
        console.log("Got SAS URL, uploading directly to Azure...");

        try {
          const response = await fetch(fileRecord.uploadUrl, {
            method: "PUT",
            headers: {
              "x-ms-blob-type": "BlockBlob",
              "Content-Type": file.type || "image/jpeg",
            },
            body: file, // Send raw file, not Base64
          });

          if (!response.ok) {
            throw new Error(
              `Direct upload failed with status ${response.status}: ${response.statusText}`
            );
          }

          console.log("Upload successful, notifying backend...");

          // Step 3: Notify backend that upload is complete using PATCH
          await azureBlobStorageStore.patch(fileRecord.id, {
            uploadComplete: true,
            actualFileSize: file.size,
          });

          console.log("Attachment uploaded successfully!");

          // Return the attachment object matching the schema
          return {
            url: fileRecord.blobUrl,
            name: file.name,
            type: file.type,
            size: file.size,
          };
        } catch (uploadError: any) {
          console.error("Upload failed:", uploadError);

          // Notify backend of failure using PATCH
          await azureBlobStorageStore.patch(fileRecord.id, {
            uploadComplete: false,
            uploadError: uploadError.message || "Upload failed",
          });

          throw uploadError;
        }
      } else {
        // Fallback: Use Base64 for smaller files if no SAS URL provided
        console.log("No SAS URL provided, using Base64 fallback...");

        const MAX_BASE64_SIZE = 4 * 1024 * 1024; // 4MB limit for images

        if (file.size > MAX_BASE64_SIZE) {
          throw new Error(
            `File size ${(file.size / 1024 / 1024).toFixed(
              2
            )}MB exceeds maximum upload size. Direct upload may not be configured properly.`
          );
        }

        // Convert to Base64 for fallback
        const reader = new FileReader();
        const base64Promise = new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const base64 = reader.result as string;
            const base64Data = base64.split(",")[1];
            resolve(base64Data);
          };
          reader.onerror = reject;
        });
        reader.readAsDataURL(file);
        const base64Data = await base64Promise;

        // Create with Base64 data
        const createdFile = await azureBlobStorageStore.create({
          ...uploadData,
          file: base64Data,
          requestBlobUrl: false, // Don't request blob URL for Base64 upload
        });

        // Return the attachment object matching the schema
        return {
          url: createdFile.blobUrl || createdFile.fileName,
          name: file.name,
          type: file.type,
          size: file.size,
        };
      }
    } catch (error) {
      console.error("Error uploading attachment:", error);
      throw error;
    }
  };

  submitIssue = async () => {
    if (!this.validateForm()) {
      console.log("Form is invalid", this.validationErrors);
      return false;
    }

    this.isSubmitting = true;
    try {
      const store = this.rootStore.fieldActivityIssuesStore as any;

      if (this.isEditMode && this.selectedIssueId) {
        // Update existing issue
        // Attachments are already properly formatted as an array with url field
        await store.patch(this.selectedIssueId, this.formData);
      } else {
        // Create new issue
        const currentUser = this.rootStore.authStore?.currentUser;
        const issueData: CreateFieldActivityIssue = {
          ...(this.formData as CreateFieldActivityIssue),
          reportedBy: this.formData.reportedBy || currentUser?.id || 0,
        };
        // Attachments are already properly formatted as an array with url field
        await store.create(issueData);
      }

      runInAction(() => {
        this.closeDialogs();
        this.isSubmitting = false;
      });
      return true;
    } catch (error) {
      console.error("Error submitting issue:", error);
      runInAction(() => {
        this.isSubmitting = false;
      });
      return false;
    }
  };

  updateIssueStatus = async (issueId: number, newStatus: IssueStatus) => {
    try {
      const store = this.rootStore.fieldActivityIssuesStore as any;
      const updateData: UpdateFieldActivityIssue = {
        status: newStatus,
      };

      // Add resolution data if resolving
      if (newStatus === "resolved" || newStatus === "closed") {
        const currentUser = this.rootStore.authStore?.currentUser;
        updateData.actualResolutionDate = new Date().toISOString();
        updateData.resolvedBy = currentUser?.id;
      }

      // Add start time if starting work
      if (newStatus === "inProgress") {
        updateData.startedAt = new Date().toISOString();
      }

      await store.patch(issueId, updateData);
      return true;
    } catch (error) {
      console.error("Error updating issue status:", error);
      return false;
    }
  };

  loadIssues = async (jobId?: number) => {
    try {
      const store = this.rootStore.fieldActivityIssuesStore as any;
      const query: any = {
        $limit: 500,
        $sort: { createdAt: -1 },
      };

      if (jobId) {
        query.jobId = jobId;
      }

      // Apply filters
      if (this.filters.status?.length) {
        query.status = { $in: this.filters.status };
      }
      if (this.filters.severity?.length) {
        query.severity = { $in: this.filters.severity };
      }
      if (this.filters.category?.length) {
        query.category = { $in: this.filters.category };
      }
      if (this.filters.safetyIssue !== undefined) {
        query.safetyIssue = this.filters.safetyIssue;
      }
      if (this.filters.qualityIssue !== undefined) {
        query.qualityIssue = this.filters.qualityIssue;
      }
      if (this.filters.assignedTo) {
        query.assignedTo = this.filters.assignedTo;
      }

      await store.find({ query });
    } catch (error) {
      console.error("Error loading issues:", error);
    }
  };

  // Computed values
  get selectedIssue(): FieldActivityIssue | undefined {
    if (!this.selectedIssueId) return undefined;
    const store = this.rootStore.fieldActivityIssuesStore as any;
    if (!store?.entities) return undefined;
    return store.entities.find((issue: FieldActivityIssue) => issue.id === this.selectedIssueId);
  }

  get issues(): FieldActivityIssue[] {
    const store = this.rootStore.fieldActivityIssuesStore as any;
    if (!store?.entities) return [];

    let issues = [...store.entities];

    // Apply filters
    if (this.filters.status?.length) {
      issues = issues.filter((issue: FieldActivityIssue) =>
        this.filters.status?.includes(issue.status as IssueStatus)
      );
    }
    if (this.filters.severity?.length) {
      issues = issues.filter((issue: FieldActivityIssue) =>
        this.filters.severity?.includes(issue.severity as Severity)
      );
    }
    if (this.filters.category?.length) {
      issues = issues.filter(
        (issue: FieldActivityIssue) =>
          issue.category &&
          this.filters.category?.includes(issue.category as IssueCategory)
      );
    }
    if (this.filters.safetyIssue !== undefined) {
      issues = issues.filter(
        (issue: FieldActivityIssue) =>
          issue.safetyIssue === this.filters.safetyIssue
      );
    }
    if (this.filters.qualityIssue !== undefined) {
      issues = issues.filter(
        (issue: FieldActivityIssue) =>
          issue.qualityIssue === this.filters.qualityIssue
      );
    }

    // Apply search
    if (this.searchTerm) {
      const searchLower = this.searchTerm.toLowerCase();
      issues = issues.filter(
        (issue: FieldActivityIssue) =>
          issue.title.toLowerCase().includes(searchLower) ||
          issue.description.toLowerCase().includes(searchLower) ||
          issue.issueType.toLowerCase().includes(searchLower)
      );
    }

    // Sort by priority and creation date
    return issues.sort((a: FieldActivityIssue, b: FieldActivityIssue) => {
      // First sort by severity
      const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      const severityDiff =
        severityOrder[a.severity] - severityOrder[b.severity];
      if (severityDiff !== 0) return severityDiff;

      // Then by creation date (newest first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }

  get issueCounts() {
    const store = this.rootStore.fieldActivityIssuesStore as any;
    const issues = store?.entities || [];

    return {
      total: issues.length,
      open: issues.filter((i: FieldActivityIssue) => i.status === "open")
        .length,
      inProgress: issues.filter(
        (i: FieldActivityIssue) => i.status === "inProgress"
      ).length,
      monitoring: issues.filter(
        (i: FieldActivityIssue) => i.status === "monitoring"
      ).length,
      resolved: issues.filter(
        (i: FieldActivityIssue) => i.status === "resolved"
      ).length,
      closed: issues.filter((i: FieldActivityIssue) => i.status === "closed")
        .length,
      critical: issues.filter(
        (i: FieldActivityIssue) => i.severity === "critical"
      ).length,
      high: issues.filter((i: FieldActivityIssue) => i.severity === "high")
        .length,
      medium: issues.filter((i: FieldActivityIssue) => i.severity === "medium")
        .length,
      low: issues.filter((i: FieldActivityIssue) => i.severity === "low")
        .length,
      safety: issues.filter((i: FieldActivityIssue) => i.safetyIssue).length,
      quality: issues.filter((i: FieldActivityIssue) => i.qualityIssue).length,
    };
  }

  get jobs() {
    return (this.rootStore.jobStore as any)?.entities || [];
  }

  get panels() {
    return (this.rootStore.panelStore as any)?.entities || [];
  }

  get users() {
    return (this.rootStore.userStore as any)?.entities || [];
  }

  get teams() {
    return (this.rootStore.departmentTeamsStore as any)?.entities || [];
  }
}

export default FieldActivityIssuesViewStore;
